import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, scan } from 'rxjs/operators';

/**
 * State for code block detection and buffering
 */
interface CodeBlockState {
  inBlock: boolean;
  type: 'unknown' | 'mermaid' | 'other';
  content: string;
}

/**
 * Internal buffer state with all tracking information
 */
interface InternalBufferState {
  displayContent: string;
  buffer: string;
  codeBlock: CodeBlockState;
  isComplete: boolean;
}

/**
 * Public buffer state exposed to consumers
 */
export interface BufferState {
  displayContent: string;
  isComplete: boolean;
}

/**
 * StreamContentBufferService - Intelligent buffering for streaming AI responses
 * 
 * ## Purpose:
 * This service handles the complex buffering logic required when streaming AI responses
 * that contain markdown content, especially code blocks and mermaid diagrams.
 * 
 * ## Why it's needed:
 * When AI responses are streamed character-by-character or in small chunks, we face
 * several challenges:
 * 
 * 1. **Markdown Delimiters**: Markdown syntax like **bold** or *italic* can be split
 *    across chunks. Displaying "**bo" followed by "ld**" would show the raw asterisks
 *    instead of formatted text.
 * 
 * 2. **Mermaid Diagrams**: Mermaid diagram syntax is sensitive to completeness. A partial
 *    mermaid diagram will fail to render and show error messages. We must buffer the
 *    entire diagram until the closing ``` is received.
 * 
 * 3. **Code Block Detection**: We need to distinguish between regular code blocks (which
 *    can be streamed immediately) and mermaid code blocks (which must be buffered).
 * 
 * ## How it works:
 * - Monitors incoming chunks for markdown delimiters and code blocks
 * - Buffers incomplete markdown syntax until it's complete
 * - Detects mermaid code blocks by language identifier or content patterns
 * - Buffers entire mermaid blocks until they're complete
 * - Flushes regular content at safe word boundaries
 * 
 * ## Usage:
 * ```typescript
 * const chunks$ = streamProcessor.streamEvent$.pipe(
 *   filter(event => event.type === StreamEventType.CHUNK_RECEIVED),
 *   map(event => event.data?.content || '')
 * );
 * 
 * streamContentBuffer.processStream(chunks$).subscribe(state => {
 *   displayContent = state.displayContent;
 * });
 * ```
 * 
 * @example
 * Input chunks: ["Here is ", "**bo", "ld** text"]
 * Output states: 
 *   1. { displayContent: "Here is ", isComplete: false }
 *   2. { displayContent: "Here is ", isComplete: false }  // buffering **bold
 *   3. { displayContent: "Here is **bold** text", isComplete: false }
 * 
 * @example
 * Input chunks: ["```mer", "maid\ngraph TD\n  A-->B\n```", "\nDone!"]
 * Output states:
 *   1. { displayContent: "", isComplete: false }  // buffering mermaid
 *   2. { displayContent: "```mermaid\ngraph TD\n  A-->B\n```", isComplete: false }
 *   3. { displayContent: "```mermaid\ngraph TD\n  A-->B\n```\nDone!", isComplete: false }
 */
@Injectable({
  providedIn: 'root'
})
export class StreamContentBufferService {

  /**
   * Process a stream of chunks into properly buffered content
   * 
   * @param chunks$ - Observable stream of text chunks from the AI response
   * @returns Observable of BufferState with displayable content and completion status
   * 
   * This is the main entry point. It uses RxJS scan operator to accumulate state
   * across all chunks, ensuring we maintain context about what we're buffering.
   */
  processStream(chunks$: Observable<string>): Observable<BufferState> {
    return chunks$.pipe(
      scan((state: InternalBufferState, chunk: string) => {
        return this.processChunk(state, chunk);
      }, this.getInitialState()),
      map(state => ({
        displayContent: state.displayContent,
        isComplete: state.isComplete
      }))
    );
  }

  /**
   * Complete any remaining buffers and return final content
   */
  completeStream(state: BufferState, remainingBuffer = ''): string {
    return state.displayContent + remainingBuffer;
  }

  private getInitialState(): InternalBufferState {
    return {
      displayContent: '',
      buffer: '',
      codeBlock: {
        inBlock: false,
        type: 'unknown',
        content: ''
      },
      isComplete: false
    };
  }

  /**
   * Process a single chunk of text, updating the buffer state
   * 
   * This is the core logic that decides what to display immediately vs what to buffer.
   * It handles three main scenarios:
   * 1. We're inside a code block (mermaid or determining type)
   * 2. We're starting a new code block
   * 3. We're processing normal markdown content
   */
  private processChunk(state: InternalBufferState, chunk: string): InternalBufferState {
    // Add chunk to buffer
    const fullBuffer = state.buffer + chunk;

    // If we're in a code block, handle it
    if (state.codeBlock.inBlock) {
      // If type is still unknown, try to determine it again with new content
      if (state.codeBlock.type === 'unknown') {
        const updatedCodeBlock = state.codeBlock.content + fullBuffer;
        const newType = this.determineCodeBlockType(updatedCodeBlock);

        if (newType === 'other') {
          // Not mermaid, flush everything and exit code block mode
          return {
            displayContent: state.displayContent + updatedCodeBlock,
            buffer: '',
            codeBlock: {
              inBlock: false,
              type: 'unknown',
              content: ''
            },
            isComplete: false
          };
        } else if (newType === 'mermaid') {
          // Now we know it's mermaid, continue buffering
          state = {
            ...state,
            codeBlock: {
              ...state.codeBlock,
              type: 'mermaid',
              content: updatedCodeBlock
            },
            buffer: ''
          };
          return this.handleMermaidBlock(state, '');
        } else {
          // Still unknown, keep buffering
          return {
            ...state,
            buffer: '',
            codeBlock: {
              ...state.codeBlock,
              content: updatedCodeBlock
            }
          };
        }
      } else if (state.codeBlock.type === 'mermaid') {
        return this.handleMermaidBlock(state, fullBuffer);
      }
    }

    // Check if we're starting a code block
    const codeBlockDetection = this.detectCodeBlockStart(state.displayContent, fullBuffer);
    if (codeBlockDetection.isStarting) {
      return this.startCodeBlock(state, fullBuffer, codeBlockDetection.position);
    }

    // Normal content processing - find safe flush point
    const safePoint = this.findSafeFlushPoint(fullBuffer);

    return {
      displayContent: state.displayContent + fullBuffer.substring(0, safePoint),
      buffer: fullBuffer.substring(safePoint),
      codeBlock: state.codeBlock,
      isComplete: false
    };
  }

  /**
   * Handle buffering for mermaid code blocks
   * 
   * Mermaid blocks must be buffered in their entirety because:
   * - Partial mermaid syntax causes rendering errors
   * - The mermaid renderer shows error messages for incomplete diagrams
   * - Users see confusing error states during streaming
   * 
   * This method accumulates the entire block until it finds the closing ```
   */
  private handleMermaidBlock(state: InternalBufferState, buffer: string): InternalBufferState {
    // Add to code block buffer
    const codeBlockContent = state.codeBlock.content + buffer;

    // Check if block is complete
    const endIndex = codeBlockContent.indexOf('\n```');
    if (endIndex !== -1) {
      // Extract everything up to and including the closing ```
      const completeBlock = codeBlockContent.substring(0, endIndex + 4); // +4 for '\n```'
      const remaining = codeBlockContent.substring(endIndex + 4);

      // Flush the complete mermaid block and reset state
      const newState: InternalBufferState = {
        displayContent: state.displayContent + completeBlock,
        buffer: remaining,
        codeBlock: {
          inBlock: false,
          type: 'unknown',
          content: ''
        },
        isComplete: false
      };

      // If there's remaining content, process it as normal content
      if (remaining) {
        return this.processChunk(newState, '');
      }

      return newState;
    }

    // Continue buffering
    return {
      ...state,
      buffer: '',
      codeBlock: {
        ...state.codeBlock,
        content: codeBlockContent
      }
    };
  }

  private detectCodeBlockStart(displayContent: string, buffer: string): { isStarting: boolean; position: number } {
    // Look for ``` in the buffer (not in already displayed content)
    const codeBlockIndex = buffer.indexOf('```');

    if (codeBlockIndex === -1) {
      return { isStarting: false, position: -1 };
    }

    // Check if this is a new code block by counting ``` in all previous content
    const fullContentBefore = displayContent + buffer.substring(0, codeBlockIndex);
    const codeBlockCount = (fullContentBefore.match(/```/g) || []).length;

    // If odd number of ``` before this one, we're closing a block
    if (codeBlockCount % 2 !== 0) {
      return { isStarting: false, position: -1 };
    }

    // This is a new code block start
    return { isStarting: true, position: codeBlockIndex };
  }

  private startCodeBlock(state: InternalBufferState, buffer: string, position: number): InternalBufferState {
    // Flush content before code block
    const beforeCodeBlock = buffer.substring(0, position);
    const codeBlockStart = buffer.substring(position);

    // Determine code block type
    const codeBlockType = this.determineCodeBlockType(codeBlockStart);

    if (codeBlockType === 'other') {
      // Not mermaid, flush everything
      return {
        displayContent: state.displayContent + buffer,
        buffer: '',
        codeBlock: {
          inBlock: false,
          type: 'unknown',
          content: ''
        },
        isComplete: false
      };
    }

    // Start buffering (mermaid or unknown)
    return {
      displayContent: state.displayContent + beforeCodeBlock,
      buffer: '',
      codeBlock: {
        inBlock: true,
        type: codeBlockType,
        content: codeBlockStart
      },
      isComplete: false
    };
  }

  /**
   * Determine the type of code block based on language identifier and content
   * 
   * Detection strategy:
   * 1. Check for explicit language identifier (```mermaid)
   * 2. Check if it's definitely NOT mermaid (```javascript, ```python, etc)
   * 3. Check for mermaid keywords in content (graph, flowchart, etc)
   * 4. Handle partial identifiers (```mer could become ```mermaid)
   * 
   * Returns 'unknown' if we need more content to determine the type
   */
  private determineCodeBlockType(codeBlockContent: string): 'unknown' | 'mermaid' | 'other' {
    const afterBackticks = codeBlockContent.substring(3);

    // Check for explicit mermaid language
    if (afterBackticks.match(/^\s*mermaid(\s|$|\n)/i)) {
      return 'mermaid';
    }

    // Check if it's definitely another language
    const langMatch = afterBackticks.match(/^\s*(\w+)(\s|\n|$)/);
    if (langMatch) {
      const language = langMatch[1].toLowerCase();

      // Check if it could be a mermaid prefix
      const mermaidPrefixes = ['m', 'me', 'mer', 'merm', 'merma', 'mermai'];
      if (!mermaidPrefixes.includes(language) && language !== 'mermaid') {
        return 'other';
      }
    }

    // Check for mermaid content patterns
    if (afterBackticks.includes('\n')) {
      const lines = afterBackticks.split('\n');

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine) {
          const mermaidKeywords = /^\s*(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|gitGraph)/i;
          if (mermaidKeywords.test(trimmedLine)) {
            return 'mermaid';
          } else {
            return 'other';
          }
        }
      }
    }

    // Still unknown
    return 'unknown';
  }

  /**
   * Find a safe position to flush content without breaking markdown syntax
   * 
   * This method ensures we don't flush in the middle of:
   * - Bold text (**text**)
   * - Italic text (*text*)
   * - Links [text](url)
   * 
   * It finds word boundaries and checks backwards from the end until it finds
   * a position where all delimiters are balanced.
   */
  private findSafeFlushPoint(text: string): number {
    // Find word boundaries
    const wordBoundaries: number[] = [];
    const boundaryRegex = /[\s\n]|[.!?,;:]\s/g;
    let match;

    while ((match = boundaryRegex.exec(text)) !== null) {
      wordBoundaries.push(match.index + match[0].length);
    }

    // Always check the beginning and end
    wordBoundaries.unshift(0);
    wordBoundaries.push(text.length);

    // Check from the last boundary backwards
    for (let i = wordBoundaries.length - 1; i >= 0; i--) {
      const position = wordBoundaries[i];
      const testString = text.substring(0, position);

      if (testString.length > 0 && !this.hasUnclosedDelimiters(testString)) {
        return position;
      }
    }

    return 0;
  }

  /**
   * Check if the text has unclosed markdown delimiters
   * 
   * We check for:
   * - Double asterisks (**) for bold text
   * - Single asterisks (*) for italic text (excluding those in **)
   * - Square brackets [] for links
   * 
   * Note: We don't check backticks (`) because code spans can be streamed
   * immediately without visual issues.
   * 
   * @returns true if there are unclosed delimiters that would cause rendering issues
   */
  private hasUnclosedDelimiters(text: string): boolean {
    // Check emphasis and link delimiters only
    const doubleAsteriskCount = (text.match(/\*\*/g) || []).length;
    const openBrackets = (text.match(/\[/g) || []).length;
    const closeBrackets = (text.match(/\]/g) || []).length;

    // For single asterisks, count those not part of double asterisks
    const textWithoutDoubleAsterisks = text.replace(/\*\*/g, '');
    const singleAsteriskCount = (textWithoutDoubleAsterisks.match(/\*/g) || []).length;

    return (doubleAsteriskCount % 2 !== 0) ||
      (singleAsteriskCount % 2 !== 0) ||
      (openBrackets !== closeBrackets);
  }
}